// Top-view silhouette of an aircraft model, for the client's pushback plan.
//
// Every triangle is flattened onto the model's XZ plane and painted into a grid
// of small cells; the covered cells are closed to remove notches under the
// close radius, the boundary of the covered area is traced into loops, the
// largest loop is smoothed and simplified. Coordinates are model metres in the
// glTF frame: +X to port, -Z toward the nose.

export interface SilhouetteMeshInput {
    // xyz triplets in mesh space
    positions: ArrayLike<number>;
    // triangle list, or null when the positions are already a triangle list
    indices: ArrayLike<number> | null;
    // 16 numbers, column-major, mesh space to aircraft space
    transform: ArrayLike<number>;
}

export interface AircraftSilhouette {
    version: number;
    cellMeters: number;
    // [x, z] pairs in model metres, one closed loop
    outline: number[][];
    portWingTip: number[];
    starboardWingTip: number[];
    noseZ: number;
    tailZ: number;
    triangles: number;
    source: string;
    /**
     * Placement of the rendered coverage mask published next to this file: a
     * `size`-pixel square spanning `size * metersPerPixel` metres, centred on
     * (centerX, centerZ) in the model frame, left edge at +X, top edge at the nose.
     */
    image?: { size: number; metersPerPixel: number; centerX: number; centerZ: number };
}

export interface SilhouetteOptions {
    source?: string;
    cellMeters?: number;
    maxCellsPerAxis?: number;
    maxTriangleAreaSquareMeters?: number;
    closeRadiusCells?: number;
    /** Explicit opening radius in cells; overrides the span-relative default. */
    openRadiusCells?: number;
    openWidthFractionOfSpan?: number;
    simplifyToleranceMeters?: number;
}

const DEFAULTS = {
    cellMeters: 0.2,
    maxCellsPerAxis: 640,
    // Larger triangles are floors, shadow planes and such, not the skin.
    maxTriangleAreaSquareMeters: 25,
    closeRadiusCells: 2,
    // Bumps narrower along the span than this fraction of the span are removed: flap
    // track fairings behind the trailing edge, wing-tip fences, antennas. The opening
    // runs along the span only, so thin airfoils (tailplane, wing tips) keep their
    // chord. Relative to the span so a light aircraft's metre-wide fuselage survives:
    // 2% is 1.2 m on a 60 m airliner and 0.2 m on a 11 m Cessna.
    openWidthFractionOfSpan: 0.02,
    openRadiusCells: undefined as number | undefined,
    simplifyToleranceMeters: 0.25
};

export function computeAircraftSilhouette(
    meshes: SilhouetteMeshInput[],
    options: SilhouetteOptions = {}
): AircraftSilhouette | null {
    const settings = { ...DEFAULTS, ...options };

    // Flatten every triangle to (x, z).
    const triangles: number[] = [];
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const mesh of meshes) {
        const m = mesh.transform;
        const p = mesh.positions;
        const vertexCount = Math.floor(p.length / 3);
        const xs = new Float64Array(vertexCount);
        const zs = new Float64Array(vertexCount);
        for (let v = 0; v < vertexCount; v++) {
            const x = p[v * 3];
            const y = p[v * 3 + 1];
            const z = p[v * 3 + 2];
            xs[v] = m[0] * x + m[4] * y + m[8] * z + m[12];
            zs[v] = m[2] * x + m[6] * y + m[10] * z + m[14];
        }
        const indexCount = mesh.indices ? mesh.indices.length : vertexCount;
        for (let i = 0; i + 2 < indexCount; i += 3) {
            const a = mesh.indices ? mesh.indices[i] : i;
            const b = mesh.indices ? mesh.indices[i + 1] : i + 1;
            const c = mesh.indices ? mesh.indices[i + 2] : i + 2;
            if (a >= vertexCount || b >= vertexCount || c >= vertexCount) continue;
            const ax = xs[a], az = zs[a], bx = xs[b], bz = zs[b], cx = xs[c], cz = zs[c];
            if (![ax, az, bx, bz, cx, cz].every(Number.isFinite)) continue;
            const area = Math.abs((bx - ax) * (cz - az) - (cx - ax) * (bz - az)) * 0.5;
            if (area > settings.maxTriangleAreaSquareMeters) continue;
            triangles.push(ax, az, bx, bz, cx, cz);
            minX = Math.min(minX, ax, bx, cx);
            maxX = Math.max(maxX, ax, bx, cx);
            minZ = Math.min(minZ, az, bz, cz);
            maxZ = Math.max(maxZ, az, bz, cz);
        }
    }
    const triangleCount = triangles.length / 6;
    if (triangleCount === 0) return null;

    // The grid, with a margin of one cell so the boundary trace has room.
    let cell = settings.cellMeters;
    cell = Math.max(cell, (maxX - minX) / (settings.maxCellsPerAxis - 2), (maxZ - minZ) / (settings.maxCellsPerAxis - 2));
    const originX = minX - cell;
    const originZ = minZ - cell;
    const width = Math.ceil((maxX - originX) / cell) + 2;
    const height = Math.ceil((maxZ - originZ) / cell) + 2;
    let covered: Uint8Array = new Uint8Array(width * height);

    const mark = (x: number, z: number) => {
        const column = Math.floor((x - originX) / cell);
        const row = Math.floor((z - originZ) / cell);
        if (column >= 0 && column < width && row >= 0 && row < height) covered[row * width + column] = 1;
    };
    const markEdge = (x0: number, z0: number, x1: number, z1: number) => {
        const steps = Math.max(1, Math.ceil(Math.hypot(x1 - x0, z1 - z0) / (cell * 0.5)));
        for (let s = 0; s <= steps; s++) {
            const t = s / steps;
            mark(x0 + (x1 - x0) * t, z0 + (z1 - z0) * t);
        }
    };
    for (let t = 0; t < triangles.length; t += 6) {
        const ax = triangles[t], az = triangles[t + 1];
        const bx = triangles[t + 2], bz = triangles[t + 3];
        const cx = triangles[t + 4], cz = triangles[t + 5];
        // Edges and corners, so slivers thinner than a cell still register.
        markEdge(ax, az, bx, bz);
        markEdge(bx, bz, cx, cz);
        markEdge(cx, cz, ax, az);
        // Interior: every cell centre inside the triangle.
        const c0 = Math.max(0, Math.floor((Math.min(ax, bx, cx) - originX) / cell));
        const c1 = Math.min(width - 1, Math.floor((Math.max(ax, bx, cx) - originX) / cell));
        const r0 = Math.max(0, Math.floor((Math.min(az, bz, cz) - originZ) / cell));
        const r1 = Math.min(height - 1, Math.floor((Math.max(az, bz, cz) - originZ) / cell));
        const d = (bx - ax) * (cz - az) - (cx - ax) * (bz - az);
        if (Math.abs(d) < 1e-12) continue;
        for (let row = r0; row <= r1; row++) {
            const pz = originZ + (row + 0.5) * cell;
            for (let column = c0; column <= c1; column++) {
                if (covered[row * width + column]) continue;
                const px = originX + (column + 0.5) * cell;
                const w0 = ((bx - px) * (cz - pz) - (cx - px) * (bz - pz)) / d;
                const w1 = ((cx - px) * (az - pz) - (ax - px) * (cz - pz)) / d;
                const w2 = 1 - w0 - w1;
                if (w0 >= 0 && w1 >= 0 && w2 >= 0) covered[row * width + column] = 1;
            }
        }
    }

    const openRadiusCells =
        settings.openRadiusCells ??
        Math.max(0, Math.round(((maxX - minX) * settings.openWidthFractionOfSpan) / 2 / cell));
    covered = close(covered, width, height, settings.closeRadiusCells);
    covered = open(covered, width, height, openRadiusCells);

    const loop = traceLargestLoop(covered, width, height);
    if (!loop || loop.length < 3) return null;

    let outline = loop.map(([column, row]) => [originX + column * cell, originZ + row * cell]);
    outline = chaikin(outline);
    outline = simplifyClosed(outline, settings.simplifyToleranceMeters);
    if (outline.length < 3) return null;

    let portWingTip = outline[0];
    let starboardWingTip = outline[0];
    let noseZ = Infinity;
    let tailZ = -Infinity;
    for (const point of outline) {
        if (point[0] > portWingTip[0]) portWingTip = point;
        if (point[0] < starboardWingTip[0]) starboardWingTip = point;
        noseZ = Math.min(noseZ, point[1]);
        tailZ = Math.max(tailZ, point[1]);
    }
    const round = (value: number) => Math.round(value * 1000) / 1000;
    return {
        version: 1,
        cellMeters: round(cell),
        outline: outline.map(([x, z]) => [round(x), round(z)]),
        portWingTip: [round(portWingTip[0]), round(portWingTip[1])],
        starboardWingTip: [round(starboardWingTip[0]), round(starboardWingTip[1])],
        noseZ: round(noseZ),
        tailZ: round(tailZ),
        triangles: triangleCount,
        source: settings.source ?? ''
    };
}

// Dilate then erode with a square of the given radius: closes gaps and notches
// up to twice the radius wide. Separable, so two passes per step.
function close(grid: Uint8Array, width: number, height: number, radius: number): Uint8Array {
    if (radius <= 0) return grid;
    return morph(morph(grid, width, height, radius, true), width, height, radius, false);
}

// Erode then dilate along the span (x) only, by the given radius: removes bumps
// narrower than twice the radius across the span, such as flap track fairings, and
// leaves anything wider exactly as it was. Nothing changes along the fuselage
// axis, so a thin airfoil keeps its full chord.
function open(grid: Uint8Array, width: number, height: number, radius: number): Uint8Array {
    if (radius <= 0) return grid;
    return morphAlongX(morphAlongX(grid, width, height, radius, false), width, height, radius, true);
}

function morphAlongX(grid: Uint8Array, width: number, height: number, radius: number, dilate: boolean): Uint8Array {
    const result = new Uint8Array(grid.length);
    for (let row = 0; row < height; row++) {
        for (let column = 0; column < width; column++) {
            let value = dilate ? 0 : 1;
            for (let dx = -radius; dx <= radius; dx++) {
                const x = column + dx;
                const inside = x >= 0 && x < width && grid[row * width + x] === 1;
                if (dilate && inside) {
                    value = 1;
                    break;
                }
                if (!dilate && !inside) {
                    value = 0;
                    break;
                }
            }
            result[row * width + column] = value;
        }
    }
    return result;
}

function morph(grid: Uint8Array, width: number, height: number, radius: number, dilate: boolean): Uint8Array {
    const horizontal = new Uint8Array(grid.length);
    for (let row = 0; row < height; row++) {
        for (let column = 0; column < width; column++) {
            let value = dilate ? 0 : 1;
            for (let dx = -radius; dx <= radius; dx++) {
                const x = column + dx;
                const inside = x >= 0 && x < width && grid[row * width + x] === 1;
                if (dilate && inside) {
                    value = 1;
                    break;
                }
                if (!dilate && !inside) {
                    value = 0;
                    break;
                }
            }
            horizontal[row * width + column] = value;
        }
    }
    const result = new Uint8Array(grid.length);
    for (let row = 0; row < height; row++) {
        for (let column = 0; column < width; column++) {
            let value = dilate ? 0 : 1;
            for (let dy = -radius; dy <= radius; dy++) {
                const y = row + dy;
                const inside = y >= 0 && y < height && horizontal[y * width + column] === 1;
                if (dilate && inside) {
                    value = 1;
                    break;
                }
                if (!dilate && !inside) {
                    value = 0;
                    break;
                }
            }
            result[row * width + column] = value;
        }
    }
    return result;
}

// Boundary edges of the covered cells, covered side on the left, chained into
// loops; the loop enclosing the most area is returned in cell-corner units.
function traceLargestLoop(covered: Uint8Array, width: number, height: number): number[][] | null {
    const at = (column: number, row: number) => column >= 0 && column < width && row >= 0 && row < height && covered[row * width + column] === 1;
    const stride = width + 1;
    const key = (x: number, y: number) => y * stride + x;
    const edges = new Map<number, number[]>();
    const add = (x0: number, y0: number, x1: number, y1: number) => {
        const from = key(x0, y0);
        const list = edges.get(from);
        if (list) list.push(key(x1, y1));
        else edges.set(from, [key(x1, y1)]);
    };
    for (let row = 0; row < height; row++) {
        for (let column = 0; column < width; column++) {
            if (!covered[row * width + column]) continue;
            if (!at(column - 1, row)) add(column, row + 1, column, row);
            if (!at(column, row - 1)) add(column, row, column + 1, row);
            if (!at(column + 1, row)) add(column + 1, row, column + 1, row + 1);
            if (!at(column, row + 1)) add(column + 1, row + 1, column, row + 1);
        }
    }

    let best: number[][] | null = null;
    let bestArea = 0;
    while (edges.size > 0) {
        const start = edges.keys().next().value as number;
        const loop: number[][] = [];
        let current = start;
        let guard = 0;
        while (guard++ < 4000000) {
            const next = edges.get(current);
            if (!next || next.length === 0) break;
            loop.push([current % stride, Math.floor(current / stride)]);
            const to = next.shift() as number;
            if (next.length === 0) edges.delete(current);
            current = to;
            if (current === start) break;
        }
        if (loop.length < 3) continue;
        let area = 0;
        for (let i = 0; i < loop.length; i++) {
            const p = loop[i];
            const q = loop[(i + 1) % loop.length];
            area += p[0] * q[1] - q[0] * p[1];
        }
        if (area > bestArea) {
            bestArea = area;
            best = loop;
        }
    }
    return best;
}

function chaikin(points: number[][]): number[][] {
    const result: number[][] = [];
    for (let i = 0; i < points.length; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        result.push([a[0] + (b[0] - a[0]) * 0.25, a[1] + (b[1] - a[1]) * 0.25]);
        result.push([a[0] + (b[0] - a[0]) * 0.75, a[1] + (b[1] - a[1]) * 0.75]);
    }
    return result;
}

function simplifyClosed(points: number[][], tolerance: number): number[][] {
    if (points.length < 8) return points;
    // Split at the point farthest from the first so both halves are open chains.
    let farthest = 0;
    let farthestDistance = 0;
    for (let i = 1; i < points.length; i++) {
        const dx = points[i][0] - points[0][0];
        const dz = points[i][1] - points[0][1];
        const distance = dx * dx + dz * dz;
        if (distance > farthestDistance) {
            farthestDistance = distance;
            farthest = i;
        }
    }
    const first = douglasPeucker(points.slice(0, farthest + 1), tolerance);
    const second = douglasPeucker(points.slice(farthest), tolerance);
    const result = first.concat(second.slice(1));
    const head = result[0];
    const tail = result[result.length - 1];
    if (result.length > 1 && Math.hypot(head[0] - tail[0], head[1] - tail[1]) < 1e-6) result.pop();
    return result;
}

function douglasPeucker(points: number[][], tolerance: number): number[][] {
    if (points.length < 3) return points;
    const first = points[0];
    const last = points[points.length - 1];
    let index = -1;
    let maxDistance = 0;
    for (let i = 1; i < points.length - 1; i++) {
        const distance = distanceToSegment(points[i], first, last);
        if (distance > maxDistance) {
            maxDistance = distance;
            index = i;
        }
    }
    if (maxDistance <= tolerance || index < 0) return [first, last];
    const left = douglasPeucker(points.slice(0, index + 1), tolerance);
    const right = douglasPeucker(points.slice(index), tolerance);
    return left.concat(right.slice(1));
}

function distanceToSegment(point: number[], a: number[], b: number[]): number {
    const dx = b[0] - a[0];
    const dz = b[1] - a[1];
    const lengthSquared = dx * dx + dz * dz;
    let t = lengthSquared > 0 ? ((point[0] - a[0]) * dx + (point[1] - a[1]) * dz) / lengthSquared : 0;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(point[0] - (a[0] + dx * t), point[1] - (a[1] + dz * t));
}
